// MentorPanel.js — панель наставника (ПК-сайдбар + список подопечных)
// Переписан 2026-09-10: подопечные берутся по ПОТОКАМ (chat-streams.json), а не по mentorId.
// Если у наставника один поток — сразу список людей; если несколько — сначала выбор потока.
// У админа — все потоки.
import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { mentorListStudents, mentorGetStudentProfile, mentorGetStudentStats, mentorListStreams } from './api';

import { Icons } from './icons';

// SVG-иконки для сайдбара
import { calculateNorms } from './normCalculator';
import { QUESTIONS } from './Questionnaire';

const StudentsIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const BackIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);
const SunIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);
const MoonIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

function MentorPanel({ onBack, getAuth }) {
  const { isDark, toggleTheme, colors } = useTheme();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  // Уведомление живёт ровно 3 секунды и исчезает само (решение владельца 2026-09-12):
  // раньше таймера не было вообще, и сообщение висело до ручного крестика.
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(t);
  }, [message]);
  // Тип уведомления — отдельным полем, а не эмодзи в начале строки (эмодзи убраны).
  const [messageKind, setMessageKind] = useState('ok');
  const showMessage = (text, kind = 'ok') => { setMessageKind(kind); setMessage(text); };
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [studentStats, setStudentStats] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list | profile | stats
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [statsDateInput, setStatsDateInput] = useState(new Date().toISOString().split('T')[0]);
  const [statsLoading, setStatsLoading] = useState(false);
  // Потоки: список доступных + выбранный (null = ещё не выбран)
  const [streams, setStreams] = useState([]);
  const [activeStream, setActiveStream] = useState(null);   // { id, name } | null
  const [isAdmin, setIsAdmin] = useState(false);
  // Опрос участника (только для чтения): карточка «Вопрос — ответ»
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [needStreamChoice, setNeedStreamChoice] = useState(false);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Загружает список людей. streamId === null и потоков несколько — запросит всех сразу (для админа).
  const loadStudents = useCallback(async (streamId) => {
    const auth = getAuth();
    if (!auth) { setError('Требуется авторизация'); setLoading(false); return; }
    setLoading(true); setError(null);
    const d = await mentorListStudents(auth.login, auth.token, streamId || undefined);
    if (d.success) {
      setStudents(d.students || []);
    } else {
      setError(d.error || 'Ошибка загрузки подопечных');
    }
    setLoading(false);
  }, [getAuth]);

  // Первичная загрузка: узнаём потоки и решаем, нужен ли выбор потока.
  const loadStreams = useCallback(async () => {
    const auth = getAuth();
    if (!auth) { setError('Требуется авторизация'); setLoading(false); return; }
    setLoading(true); setError(null);
    const d = await mentorListStreams(auth.login, auth.token);
    if (!d.success) {
      setError(d.error || 'Ошибка загрузки потоков');
      setLoading(false);
      return;
    }
    const list = d.streams || [];
    setStreams(list);
    setIsAdmin(!!d.isAdmin);
    setLoading(false);

    if (list.length === 0) {
      setStudents([]);
      setActiveStream(null);
      setNeedStreamChoice(false);
      return;
    }
    if (list.length === 1) {
      // Один поток — сразу люди, без лишнего клика
      setActiveStream({ id: list[0].id, name: list[0].name });
      setNeedStreamChoice(false);
      loadStudents(list[0].id);
      return;
    }
    // Несколько потоков: админу показываем всех сразу, наставнику — выбор
    setNeedStreamChoice(true);
    setActiveStream(null);
    if (d.isAdmin) loadStudents(null);
    else setStudents([]);
  }, [getAuth, loadStudents]);

  useEffect(() => { loadStreams(); }, [loadStreams]);

  // Выбор конкретного потока из списка
  const chooseStream = (st) => {
    setActiveStream({ id: st.id, name: st.name });
    setNeedStreamChoice(false);
    setSelectedStudent(null);
    setStudentProfile(null);
    setStudentStats(null);
    setViewMode('list');
    loadStudents(st.id);
  };

  // Вернуться к списку потоков (если их несколько)
  const backToStreams = () => {
    setActiveStream(null);
    setSelectedStudent(null);
    setStudentProfile(null);
    setStudentStats(null);
    setViewMode('list');
    if (streams.length > 1) {
      setNeedStreamChoice(true);
      if (isAdmin) loadStudents(null); else setStudents([]);
    } else {
      loadStreams();
    }
  };

  // Кнопка «Копировать» вместо старой «отвязать» (решение владельца 2026-09-12).
  // В буфер идёт: логин, весь профиль и опрос (если пройден; если нет — пометка
  // «Опрос: не пройден»). Профиль подтягиваем отдельным запросом: в списке
  // подопечных есть только логин, имя и дата.
  const [copyBusy, setCopyBusy] = useState(null);

  const formatQuestionnaireForCopy = (qn) => {
    if (!qn || !qn.completed) return 'Опрос: не пройден';
    const answers = qn.answers || {};
    const lines = QUESTIONS.map(q => {
      const raw = (answers[String(q.id)] || '').trim();
      return `${q.id}. ${q.title}\n   ${raw ? raw : 'Не ответил'}`;
    });
    const when = qn.updatedAt ? ` (от ${new Date(qn.updatedAt).toLocaleDateString('ru-RU')})` : '';
    return `Опрос: пройден${when}\n${lines.join('\n')}`;
  };

  const handleCopyStudent = async (studentLogin) => {
    const auth = getAuth();
    if (!auth) return;
    setCopyBusy(studentLogin);
    try {
      const d = await mentorGetStudentProfile(auth.login, auth.token, studentLogin, activeStream ? activeStream.id : undefined);
      if (!d.success) { showMessage(d.error || 'Ошибка', 'err'); setCopyBusy(null); return; }
      const p = d.profile || {};
      const f = (label, value, unit) => `${label}: ${value !== null && value !== undefined && value !== '' ? value + (unit || '') : '—'}`;

      const lines = [
        `Логин: ${studentLogin}`,
        '',
        '— ПРОФИЛЬ —',
        f('Рост', p.height, ' см'),
        f('Вес', p.weight, ' кг'),
        f('Возраст', p.age),
        f('Пол', p.gender),
        f('Активность', p.activity),
        f('Талия', p.waist, ' см'),
        f('Целевой вес', p.targetWeight, ' кг'),
        f('Вода в день', p.dailyWater, ' мл'),
        f('Сон', p.sleepHours, ' ч'),
        f('Шаги в день', p.dailySteps),
        f('Бег км', p.runKm),
        f('Бег мин', p.runMin),
        f('Любимые белки', (p.favoriteProteins || []).join(', ')),
        f('Исключённые белки', (p.excludedProteins || []).join(', ')),
      ];

      if (Array.isArray(p.weightHistory) && p.weightHistory.length) {
        lines.push('', '— ИСТОРИЯ ВЕСА —');
        p.weightHistory.forEach(w => lines.push(`   ${w.date}: ${w.weight} кг`));
      }
      if (Array.isArray(p.waistHistory) && p.waistHistory.length) {
        lines.push('', '— ИСТОРИЯ ТАЛИИ —');
        p.waistHistory.forEach(w => lines.push(`   ${w.date}: ${w.waist} см`));
      }

      lines.push('', '— ОПРОС —', formatQuestionnaireForCopy(p.questionnaire));

      const text = lines.join('\n');
      try {
        await navigator.clipboard.writeText(text);
        showMessage('Скопировано: ' + studentLogin);
      } catch (e) {
        // Фолбэк для случаев, когда clipboard API недоступен (не HTTPS, старый браузер)
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); showMessage('Скопировано: ' + studentLogin); }
        catch (e2) { showMessage('Не удалось скопировать', 'err'); }
        document.body.removeChild(ta);
      }
    } catch (e) {
      showMessage('Не удалось скопировать', 'err');
    }
    setCopyBusy(null);
  };

  const handleViewProfile = async (studentLogin) => {
    const auth = getAuth();
    if (!auth) return;
    setLoading(true);
    const d = await mentorGetStudentProfile(auth.login, auth.token, studentLogin, activeStream ? activeStream.id : undefined);
    if (d.success) {
      setStudentProfile(d.profile);
      setSelectedStudent(studentLogin);
      setViewMode('profile');
    } else {
      showMessage(d.error || 'Ошибка', 'err');
    }
    setLoading(false);
  };

  const loadStatsForDate = async (studentLogin, dateStr) => {
    const auth = getAuth();
    if (!auth) return;
    setStatsLoading(true);
    const ruDate = dateStr ? new Date(dateStr + 'T00:00:00').toLocaleDateString('ru-RU') : new Date().toLocaleDateString('ru-RU');
    const d = await mentorGetStudentStats(auth.login, auth.token, studentLogin, ruDate, activeStream ? activeStream.id : undefined);
    if (d.success) { setStudentStats(d.stats); }
    else { showMessage(d.error || 'Ошибка', 'err'); }
    setStatsLoading(false);
  };

  const handleViewStats = async (studentLogin) => {
    const auth = getAuth();
    if (!auth) return;
    // Подгружаем профиль подопечного, чтобы показать его суточную норму калорий
    const pd = await mentorGetStudentProfile(auth.login, auth.token, studentLogin, activeStream ? activeStream.id : undefined);
    if (pd.success) { setStudentProfile(pd.profile); } else { setStudentProfile(null); }
    const today = new Date().toISOString().split('T')[0];
    setStatsDateInput(today);
    setSelectedStudent(studentLogin);
    setViewMode('stats');
    await loadStatsForDate(studentLogin, today);
  };

  // Отвязка подопечного удалена 2026-09-10: людей убирает админ в разделе «Потоки».

  // Цвета
  // ===== Вспомогательные функции для карточек приёмов (как в DayStats) =====
  const getMealValue = (meal, field) => {
    if (meal[field] !== undefined && meal[field] !== null && meal[field] !== '') {
      return parseFloat(meal[field]) || 0;
    }
    const d = meal.mealText || '';
    const patterns = {
      calories: /Калории:\s*([\d.]+)\s*ккал/,
      protein: /Белки:\s*([\d.]+)\s*г/,
      fat: /Жиры:\s*([\d.]+)\s*г/,
      carbs: /Углеводы:\s*([\d.]+)\s*г/
    };
    const m = d.match(patterns[field]);
    return m ? parseFloat(m[1]) || 0 : 0;
  };
  const parseMealTypeForCard = (data) => { const m = data.match(/Тип приёма:\s*(.+)/); return m ? m[1] : 'Приём пищи'; };
  // Формат ответа AI менялся (дефис → без дефиса), поэтому читаем оба вида,
  // а служебные строки отсекаем всегда (см. то же в DayStats.js).
  const parseMealTextForCard = (data) => {
    const out = { products: [], noWeight: [] };
    if (!data) return out;
    const lines = data.split('\n');
    const pi = lines.findIndex(l => l.startsWith('Продукты:'));
    if (pi === -1) return out;
    const isService = (l) => l === '' || /^(Калории|Белки|Жиры|Углеводы|Соотношение|Не распознано|Не указан вес|Без веса|Итого)/.test(l) || l.startsWith('⚠');
    // Строки «Не распознано / Не указан вес / Без веса» не показываем: продукт без веса
    // и так виден в списке со знаком «?» (решение владельца 2026-09-17, как в DayStats.js).
    for (let i = pi + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (isService(line)) continue;
      const product = line.replace(/^[-•*]\s*/, '').trim();
      if (product) out.products.push(product);
    }
    return out;
  };
  const parseProductsForCard = (data) => parseMealTextForCard(data).products;
  void parseProductsForCard; // оставлена как точка входа совместимости, используется parseMealTextForCard
  const parseMacrosForCard = (data) => {
    const pm = data.match(/Белки:\s*([\d.]+)\s*г/);
    const fm = data.match(/Жиры:\s*([\d.]+)\s*г/);
    const cm = data.match(/Углеводы:\s*([\d.]+)\s*г/);
    return { protein: pm ? pm[1] : '0', fat: fm ? fm[1] : '0', carbs: cm ? cm[1] : '0' };
  };
  const parseCaloriesForCard = (data) => {
    const m = (data || '').match(/Калории:\s*([\d.]+)\s*ккал/);
    return m ? m[1] : '?';
  };
  const bg = isDark ? '#0f0f0f' : '#f5f5f5';
  const sidebarBg = isDark ? '#1a1a2e' : '#ffffff';
  const cardBg = isDark ? '#16213e' : '#f0f0f0';
  const accent = '#1D9E75';
  const textColor = isDark ? '#eee' : '#222';
  const mutedColor = isDark ? '#888' : '#999';
  const borderColor = isDark ? '#2a2a3e' : '#ddd';
  const hoverBg = isDark ? 'rgba(29,158,117,0.15)' : 'rgba(29,158,117,0.08)';

  // Суточная норма подопечного (для профиля и статистики) — считается из его параметров
  const studentDailyNorms = (studentProfile && studentProfile.height && studentProfile.weight
    && studentProfile.age && studentProfile.gender && studentProfile.activity)
    ? calculateNorms(studentProfile)
    : null;

  const sidebarItems = [
    { key: 'list', label: needStreamChoice ? 'Потоки' : 'Подопечные', icon: StudentsIcon, count: needStreamChoice ? streams.length : students.length },
    { key: 'back', label: 'Назад в приложение', icon: BackIcon },
  ];

  const sidebarStyle = {
    width: 220, background: sidebarBg, borderRight: `1px solid ${borderColor}`,
    display: 'flex', flexDirection: 'column', color: textColor, fontFamily: 'system-ui, sans-serif'
  };
  const headerStyle = {
    padding: '20px 20px 16px', borderBottom: `1px solid ${borderColor}`, marginBottom: 8
  };
  const navItemStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', border: 'none',
    background: isActive ? hoverBg : 'transparent', color: isActive ? accent : textColor,
    fontSize: 14, cursor: 'pointer', textAlign: 'left', width: '100%',
    borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent'
  });
  const badgeStyle = {
    marginLeft: 'auto', background: isDark ? '#333' : '#e0e0e0',
    padding: '2px 8px', borderRadius: 10, fontSize: 11, color: mutedColor
  };
  const footerStyle = {
    marginTop: 'auto', padding: '16px 20px', borderTop: `1px solid ${borderColor}`
  };
  const contentCardStyle = {
    maxWidth: isDesktop ? 1000 : '100%', margin: '0 auto', background: sidebarBg, borderRadius: isDesktop ? 16 : 12,
    padding: isDesktop ? 24 : 16, height: isDesktop ? 'calc(100vh - 48px)' : 'auto', display: 'flex', flexDirection: 'column',
    boxShadow: isDark ? 'none' : (isDesktop ? '0 2px 20px rgba(0,0,0,0.08)' : 'none')
  };
  const scrollAreaStyle = {
    flex: isDesktop ? 1 : 'none', overflowY: isDesktop ? 'auto' : 'visible', marginTop: 16, scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch',
  };
  const btnStyle = {
    padding: '8px 20px', background: accent, color: 'white', border: 'none',
    borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
  };
  const tableRowStyle = {
    display: 'grid', gridTemplateColumns: '1fr 1fr 100px',
    padding: '12px 16px', borderBottom: `1px solid ${borderColor}`,
    fontSize: 13, color: textColor, alignItems: 'center'
  };
  const tableHeaderStyle = {
    display: 'grid', gridTemplateColumns: '1fr 1fr 100px',
    padding: '10px 16px', borderBottom: `1px solid ${borderColor}`,
    fontSize: 11, color: mutedColor, fontWeight: 600
  };

  return (
    <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', minHeight: '100vh', background: bg }}>
      {/* Сайдбар — только на ПК */}
      {isDesktop && <div style={sidebarStyle}>
        <div style={headerStyle}>
          <div style={{ fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icons.profile size={18} color={accent} /> Панель наставника
          </div>
        </div>
        {sidebarItems.map(item => (
          <button key={item.key} style={navItemStyle(viewMode === item.key || (item.key === 'list' && viewMode === 'list'))}
            onClick={() => {
              if (item.key === 'back') { onBack(); return; }
              setViewMode(item.key);
              setSelectedStudent(null);
              setStudentProfile(null);
              setStudentStats(null);
              // Возврат к списку: если потоков несколько — снова выбор потока
              if (item.key === 'list' && streams.length > 1) {
                setNeedStreamChoice(true);
                setActiveStream(null);
                if (isAdmin) loadStudents(null); else setStudents([]);
              }
            }}>
            <item.icon color={viewMode === item.key || (item.key === 'list' && viewMode === 'list') ? accent : mutedColor} />
            <span>{item.label}</span>
            {item.count !== undefined && <span style={badgeStyle}>{item.count}</span>}
          </button>
        ))}
        <div style={footerStyle}>
          <button onClick={toggleTheme} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', color: mutedColor, fontSize: 13, cursor: 'pointer', padding: 0 }}>
            {isDark ? <SunIcon color={mutedColor} /> : <MoonIcon color={mutedColor} />}
            {isDark ? 'Светлая тема' : 'Тёмная тема'}
          </button>
        </div>
      </div>}

      {/* Шапка на мобилке — без кнопки назад и без темы */}
      {!isDesktop && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: sidebarBg, borderBottom: `1px solid ${borderColor}`, color: textColor }}>
          {needStreamChoice && streams.length > 1 && (
            <button onClick={backToStreams} style={{ padding: '4px 8px', background: 'transparent', border: `1px solid ${borderColor}`, borderRadius: 8, color: textColor, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Icons.back size={14} color={textColor} /></button>
          )}
          <StudentsIcon color={accent} />
          <div style={{ fontSize: 16, fontWeight: 600, flex: 1 }}>
            {needStreamChoice ? (isAdmin ? 'Все потоки' : 'Мои потоки') : 'Подопечные'}
            {!needStreamChoice && activeStream ? <span style={{ color: accent, fontWeight: 500, fontSize: 14 }}> · {activeStream.name}</span> : null}
          </div>
        </div>
      )}

      {/* Контент */}
      <div style={{ flex: 1, padding: isDesktop ? 24 : 12, overflowY: 'auto' }}>
        <div style={contentCardStyle}>
          {message && (
            <div style={{ padding: '10px 16px', borderRadius: 12, fontSize: 14, textAlign: 'center',
              background: messageKind === 'ok' ? (isDark ? '#1a3a1a' : '#e8f5e9') : (isDark ? '#5c1a1a' : '#ffebee'),
              color: messageKind === 'ok' ? '#27ae60' : '#e53935', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {messageKind === 'ok'
                ? <Icons.check size={16} color="#27ae60" />
                : <Icons.warning size={16} color="#e53935" />}
              <span>{message}</span>
            </div>
          )}

          {/* ВЫБОР ПОТОКА (когда потоков несколько) */}
          {viewMode === 'list' && needStreamChoice && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginBottom: 16 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: textColor }}>{isAdmin ? 'Все потоки' : 'Мои потоки'}</h2>
                <button onClick={loadStreams} disabled={loading} style={btnStyle}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {loading ? '...' : 'Обновить'}
                </button>
              </div>
              {loading && <div style={{ color: mutedColor, textAlign: 'center', padding: 40 }}>Загрузка...</div>}
              {error && <div style={{ background: isDark ? '#5c1a1a' : '#ffebee', color: '#e53935', padding: 16, borderRadius: 12, fontSize: 14 }}>{error}</div>}
              {!loading && (
                <div style={scrollAreaStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {streams.map(st => {
                      const visibleMembers = (st.members || []).filter(m => m !== (getAuth() ? getAuth().login : null));
                      return (
                        <button key={st.id} onClick={() => chooseStream(st)}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', width: '100%',
                            padding: '14px 16px', background: cardBg, borderRadius: 12, border: `1px solid ${borderColor}`,
                            color: textColor, cursor: 'pointer' }}>
                          <StudentsIcon color={accent} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.name}</div>
                            <div style={{ fontSize: 11, color: mutedColor }}>участников: {visibleMembers.length}</div>
                          </div>
                          <span style={{ color: mutedColor }}>›</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* СПИСОК ПОДОПЕЧНЫХ */}
          {viewMode === 'list' && !needStreamChoice && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginBottom: 16, flexWrap: 'wrap' }}>
                {streams.length > 1 && (
                  <button onClick={backToStreams} style={{ padding: '6px 12px', background: 'transparent', border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer', color: textColor, fontSize: 13 }}>
                    <Icons.back size={14} color={textColor} /> Потоки
                  </button>
                )}
                <h2 style={{ fontSize: 18, fontWeight: 600, color: textColor }}>
                  Подопечные{activeStream ? <span style={{ color: accent, fontWeight: 500 }}> · {activeStream.name}</span> : null}
                </h2>
                <button onClick={() => loadStudents(activeStream ? activeStream.id : null)} disabled={loading} style={btnStyle}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {loading ? '...' : 'Обновить'}
                </button>
              </div>

              {loading && <div style={{ color: mutedColor, textAlign: 'center', padding: 40 }}>Загрузка...</div>}
              {error && <div style={{ background: isDark ? '#5c1a1a' : '#ffebee', color: '#e53935', padding: 16, borderRadius: 12, fontSize: 14 }}>{error}</div>}

              {!loading && !error && students.length === 0 && (
                <div style={{ color: mutedColor, textAlign: 'center', padding: 60, fontSize: 14 }}>
                  <div style={{ marginBottom: 16, opacity: 0.3 }}><Icons.users size={44} color={textColor} /></div>
                  В этом потоке пока никого нет.<br />Участников добавляет админ в разделе «Потоки».
                </div>
              )}

              {!loading && students.length > 0 && (
                <div style={scrollAreaStyle}>
                  <div style={{ background: cardBg, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={tableHeaderStyle}>
                      <span>Имя / Логин</span><span>Зарегистрирован</span><span>Действия</span>
                    </div>
                    {students.map(s => (
                      <div key={s.login} style={tableRowStyle}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: mutedColor }}>{s.login}</div>
                        </div>
                        <div style={{ fontSize: 12, color: mutedColor }}>
                          {s.registeredAt ? new Date(s.registeredAt).toLocaleDateString('ru-RU') : '—'}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => handleViewStats(s.login)} style={{ padding: '4px 8px', background: 'transparent', border: `1px solid ${mutedColor}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Статистика">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
                            </svg>
                          </button>
                          <button onClick={() => handleViewProfile(s.login)} style={{ padding: '4px 8px', background: 'transparent', border: `1px solid ${mutedColor}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Профиль">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                          </button>
                          {/* Копировать: логин + профиль + опрос в буфер (замена старой «отвязать») */}
                          <button onClick={() => handleCopyStudent(s.login)} disabled={copyBusy === s.login}
                            style={{ padding: '4px 8px', background: 'transparent', border: `1px solid ${mutedColor}`, borderRadius: 6, cursor: copyBusy === s.login ? 'default' : 'pointer', display: 'flex', alignItems: 'center', opacity: copyBusy === s.login ? 0.5 : 1 }}
                            title={copyBusy === s.login ? 'Копирование…' : 'Копировать данные подопечного'}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ПРОФИЛЬ ПОДОПЕЧНОГО */}
          {viewMode === 'profile' && studentProfile && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => { setViewMode('list'); setStudentProfile(null); setSelectedStudent(null); }}
                  style={{ padding: '6px 12px', background: 'transparent', border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer', color: textColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icons.back size={14} color={textColor} /> Назад
                </button>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: textColor }}>Профиль: <span style={{ color: accent }}>{selectedStudent}</span></h2>
              </div>
              <div style={scrollAreaStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { label: 'Рост', value: studentProfile.height ? `${studentProfile.height} см` : '—' },
                    { label: 'Вес', value: studentProfile.weight ? `${studentProfile.weight} кг` : '—' },
                    { label: 'Возраст', value: studentProfile.age || '—' },
                    { label: 'Пол', value: studentProfile.gender || '—' },
                    { label: 'Активность', value: studentProfile.activity || '—' },
                    { label: 'Талия', value: studentProfile.waist ? `${studentProfile.waist} см` : '—' },
                    { label: 'Целевой вес', value: studentProfile.targetWeight ? `${studentProfile.targetWeight} кг` : '—' },
                    { label: 'Вода в день', value: studentProfile.dailyWater ? `${studentProfile.dailyWater} мл` : '—' },
                    { label: 'Сон', value: studentProfile.sleepHours ? `${studentProfile.sleepHours} ч` : '—' },
                    { label: 'Шаги', value: studentProfile.dailySteps || '—' },
                    { label: 'Бег (км)', value: studentProfile.runKm || '—' },
                    { label: 'Бег (мин)', value: studentProfile.runMin || '—' },
                  ].map((f, i) => (
                    <div key={i} style={{ background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                      <div style={{ fontSize: 11, color: mutedColor, marginBottom: 4 }}>{f.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 500, color: textColor }}>{f.value}</div>
                    </div>
                  ))}
                </div>

                {/* Опросник участника — статус и кнопка просмотра (только чтение) */}
                {(() => {
                  const qn = studentProfile.questionnaire;
                  const done = !!(qn && qn.completed);
                  const answers = (qn && qn.answers) || {};
                  return (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                        <div>
                          <div style={{ fontSize: 11, color: mutedColor, marginBottom: 4 }}>Опрос</div>
                          <div style={{ fontSize: 15, fontWeight: 500, color: done ? accent : textColor }}>
                            {done ? 'Пройден' : 'Не пройден'}
                          </div>
                          {done && qn.updatedAt && (
                            <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
                              {new Date(qn.updatedAt).toLocaleDateString('ru-RU')}
                            </div>
                          )}
                        </div>
                        <button
                          disabled={!done}
                          onClick={() => setShowQuestionnaire(true)}
                          style={{
                            padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 500,
                            border: `1px solid ${done ? accent : borderColor}`,
                            background: done ? 'rgba(29,158,117,0.12)' : 'transparent',
                            color: done ? accent : mutedColor,
                            cursor: done ? 'pointer' : 'default'
                          }}>
                          Посмотреть ответы
                        </button>
                      </div>

                      {showQuestionnaire && done && (
                        <div style={{ marginTop: 12, background: cardBg, borderRadius: 12, padding: '4px 0' }}>
                          {QUESTIONS.map((q, i) => {
                            const raw = answers[String(q.id)];
                            const text = (raw || '').trim();
                            const isSkipped = !text;
                            return (
                              <div key={q.id} style={{ padding: '12px 16px', borderBottom: i < QUESTIONS.length - 1 ? `1px solid ${borderColor}` : 'none' }}>
                                <div style={{ fontSize: 12, color: mutedColor, marginBottom: 4 }}>{q.id}. {q.title}</div>
                                <div style={{ fontSize: 14, color: isSkipped ? mutedColor : textColor, fontStyle: isSkipped ? 'italic' : 'normal', whiteSpace: 'pre-wrap' }}>
                                  {isSkipped ? 'Не ответил' : text}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Суточные нормы подопечного */}
                {studentDailyNorms && (
                  <div style={{ marginTop: 20 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: textColor, marginBottom: 8 }}>Суточные нормы</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                        <div style={{ fontSize: 11, color: mutedColor, marginBottom: 4 }}>Калории</div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: accent }}>{studentDailyNorms.calories} ккал</div>
                      </div>
                      <div style={{ background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                        <div style={{ fontSize: 11, color: mutedColor, marginBottom: 4 }}>Белки</div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: textColor }}>{studentDailyNorms.protein} г</div>
                      </div>
                      <div style={{ background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                        <div style={{ fontSize: 11, color: mutedColor, marginBottom: 4 }}>Жиры</div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: textColor }}>{studentDailyNorms.fat} г</div>
                      </div>
                      <div style={{ background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                        <div style={{ fontSize: 11, color: mutedColor, marginBottom: 4 }}>Углеводы</div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: textColor }}>{studentDailyNorms.carbs} г</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* История веса */}
                {studentProfile.weightHistory && studentProfile.weightHistory.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: textColor, marginBottom: 8 }}>История веса</h3>
                    <div style={{ background: cardBg, borderRadius: 12, overflow: 'hidden' }}>
                      {studentProfile.weightHistory.slice(-10).map((entry, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', borderBottom: i < studentProfile.weightHistory.length - 1 ? `1px solid ${borderColor}` : 'none', fontSize: 13 }}>
                          <span style={{ color: mutedColor }}>{entry.date}</span>
                          <span style={{ fontWeight: 500, color: textColor }}>{entry.weight} кг</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* История талии */}
                {studentProfile.waistHistory && studentProfile.waistHistory.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: textColor, marginBottom: 8 }}>История талии</h3>
                    <div style={{ background: cardBg, borderRadius: 12, overflow: 'hidden' }}>
                      {studentProfile.waistHistory.slice(-10).map((entry, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', borderBottom: i < studentProfile.waistHistory.length - 1 ? `1px solid ${borderColor}` : 'none', fontSize: 13 }}>
                          <span style={{ color: mutedColor }}>{entry.date}</span>
                          <span style={{ fontWeight: 500, color: textColor }}>{entry.waist} см</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* СТАТИСТИКА ПОДОПЕЧНОГО */}
          {viewMode === 'stats' && studentStats && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <button onClick={() => { setViewMode('list'); setStudentStats(null); setSelectedStudent(null); }}
                  style={{ padding: '6px 12px', background: 'transparent', border: `1px solid ${borderColor}`, borderRadius: 8, cursor: 'pointer', color: textColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icons.back size={14} color={textColor} /> Назад
                </button>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: textColor, flex: 1 }}>
                  Статистика: <span style={{ color: accent }}>{selectedStudent}</span>
                </h2>
                {/* Календарь выбора даты */}
                <input type="date" value={statsDateInput}
                  onChange={e => { setStatsDateInput(e.target.value); loadStatsForDate(selectedStudent, e.target.value); }}
                  style={{ padding: '8px 12px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 10, background: cardBg, color: textColor, outline: 'none' }} />
              </div>

              {statsLoading && <div style={{ color: mutedColor, textAlign: 'center', padding: 20 }}>Загрузка...</div>}

              {!statsLoading && (
                <div style={scrollAreaStyle}>
                  {/* Итоги за выбранный день */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div style={{ background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                      <div style={{ fontSize: 11, color: mutedColor }}>Приёмов</div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: textColor }}>{studentStats.dayMeals}</div>
                    </div>
                    <div style={{ background: cardBg, padding: '12px 16px', borderRadius: 12 }}>
                      <div style={{ fontSize: 11, color: mutedColor }}>Калории (съедено / норма)</div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: textColor }}>
                        {studentStats.dayCalories} / {studentDailyNorms ? studentDailyNorms.calories : '—'} ккал
                      </div>
                    </div>
                  </div>

                  {/* Соотношение БЖУ */}
                  <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: textColor, marginBottom: 12 }}>Соотношение Б:Ж:У</div>
                    {(() => {
                      const total = studentStats.dayProtein * 4 + studentStats.dayFat * 9 + studentStats.dayCarbs * 4;
                      const pPct = total > 0 ? Math.round((studentStats.dayProtein * 4 / total) * 100) : 0;
                      const fPct = total > 0 ? Math.round((studentStats.dayFat * 9 / total) * 100) : 0;
                      const cPct = total > 0 ? Math.round((studentStats.dayCarbs * 4 / total) * 100) : 0;
                      return (
                        <>
                          <div style={{ display: 'flex', height: 24, borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
                            {pPct > 0 && <div style={{ width: `${pPct}%`, background: '#5DCAA5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#fff', minWidth: 24 }}>{pPct}%</div>}
                            {fPct > 0 && <div style={{ width: `${fPct}%`, background: '#FAC775', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#334455', minWidth: 24 }}>{fPct}%</div>}
                            {cPct > 0 && <div style={{ width: `${cPct}%`, background: '#85B7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#fff', minWidth: 24 }}>{cPct}%</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
                            <span style={{ color: '#5DCAA5' }}>● Белки: {studentStats.dayProtein} г ({pPct}%)</span>
                            <span style={{ color: '#FAC775' }}>● Жиры: {studentStats.dayFat} г ({fPct}%)</span>
                            <span style={{ color: '#85B7EB' }}>● Углеводы: {studentStats.dayCarbs} г ({cPct}%)</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Недельные итоги */}
                  <div style={{ background: cardBg, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: textColor, marginBottom: 12 }}>За неделю</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div style={{ fontSize: 13, color: mutedColor }}>Приёмов: <strong style={{ color: textColor }}>{studentStats.weekMeals}</strong></div>
                      <div style={{ fontSize: 13, color: mutedColor }}>Калории: <strong style={{ color: textColor }}>{studentStats.weekCalories}</strong></div>
                      <div style={{ fontSize: 13, color: mutedColor }}>Белки: <strong style={{ color: '#5DCAA5' }}>{studentStats.weekProtein} г</strong></div>
                      <div style={{ fontSize: 13, color: mutedColor }}>Жиры: <strong style={{ color: '#FAC775' }}>{studentStats.weekFat} г</strong></div>
                      <div style={{ fontSize: 13, color: mutedColor }}>Углеводы: <strong style={{ color: '#85B7EB' }}>{studentStats.weekCarbs} г</strong></div>
                      <div style={{ fontSize: 13, color: mutedColor }}>Дней: <strong style={{ color: textColor }}>{studentStats.weekDays}/7</strong></div>
                    </div>
                  </div>

                  {/* Карточки приёмов (вид как у пользователей, без кнопок редактировать/удалить) */}
                  {studentStats.meals && studentStats.meals.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 600, color: textColor, marginBottom: 8 }}>
                        Приёмы ({studentStats.meals.length})
                      </h3>
                      {studentStats.meals.slice().sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || '')).map((meal) => {
                        const cal = getMealValue(meal, 'calories');
                        const prot = getMealValue(meal, 'protein');
                        const fat = getMealValue(meal, 'fat');
                        const carb = getMealValue(meal, 'carbs');
                        const hasMacros = cal > 0 || prot > 0 || fat > 0 || carb > 0;
                        const parsedForCard = parseMealTextForCard(meal.mealText);
                        const products = parsedForCard.products;
                        const macros = parseMacrosForCard(meal.mealText);
                        const time = meal.time || (meal.timestamp ? new Date(meal.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '');
                        return (
                          <div key={meal.id || meal._id || Math.random()} style={{ padding: 16, background: colors.card, borderRadius: 16, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <div style={{ fontSize: 16, fontWeight: 600, color: '#1D9E75' }}>{parseMealTypeForCard(meal.mealText)}</div>
                              <div style={{ fontSize: 13, color: colors.textMuted }}>{time}</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                              <div style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>{cal > 0 ? `${cal} ккал` : macros.protein !== '0' ? `${parseCaloriesForCard(meal.mealText)} ккал` : '?'}</div>
                              {hasMacros && <div style={{ fontSize: 12, color: colors.textMuted, background: colors.progressBg, padding: '4px 8px', borderRadius: 6 }}>
                                Б:Ж:У = {(() => { const pk = prot*4, fk = fat*9, ck = carb*4, t = pk+fk+ck; return t ? `${Math.round(pk/t*100)}/${Math.round(fk/t*100)}/${Math.round(ck/t*100)}` : '0/0/0'; })()}
                              </div>}
                            </div>
                            <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                              <div style={{ flex: 1, color: colors.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {products.length > 0 ? products.map((p, i) => <div key={i} style={{ marginBottom: 4 }}>• {p}</div>) : <div style={{ color: colors.textMuted, fontSize: 12 }}>Приём не разобран — состав не распознан</div>}
                              </div>
                              {hasMacros && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <div style={{ color: colors.textLight }}>Белки: {prot} г</div>
                                  <div style={{ color: colors.textLight }}>Жиры: {fat} г</div>
                                  <div style={{ color: colors.textLight }}>Углеводы: {carb} г</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {(!studentStats.meals || studentStats.meals.length === 0) && (
                    <div style={{ color: mutedColor, textAlign: 'center', padding: 30, fontSize: 14 }}>
                      Нет записей за выбранный день
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Заглушка, если ничего не выбрано */}
          {viewMode !== 'list' && !studentProfile && !studentStats && (
            <div style={{ color: mutedColor, textAlign: 'center', padding: 60, fontSize: 14 }}>
              Выберите подопечного из списка
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MentorPanel;


